import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Continuous Web Speech API dictation hook tailored for long-form text
 * entry (sermon outline points, notes, etc.) — distinct from the existing
 * `useLumeraVoice` hook which is one-shot Q&A oriented.
 *
 * Differences from useLumeraVoice:
 *  - `continuous = true` so the recognizer doesn't auto-stop after a pause
 *  - `interimResults = true` so we surface a live preview transcript
 *  - On each `final` chunk we call `onFinalChunk(text)` so the consumer can
 *    append to whichever field is currently focused
 *  - Auto-restarts on `onend` (Chrome quietly stops every ~60s) until the
 *    caller toggles `stop()` explicitly
 *
 * Usage:
 *   const { isListening, interim, start, stop, toggle, supported } =
 *     useDictation({ language, onFinalChunk: (txt) => append(targetId, txt) });
 *
 * The caller is responsible for tracking which field receives the text.
 * The hook itself stays field-agnostic.
 */
export default function useDictation({ language = 'en', onFinalChunk } = {}) {
  const [isListening, setIsListening] = useState(false);
  const [interim, setInterim] = useState('');
  const recognitionRef = useRef(null);
  const wantsRunningRef = useRef(false);
  const supportedRef = useRef(false);

  const es = language === 'es';

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      supportedRef.current = false;
      return undefined;
    }
    supportedRef.current = true;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = es ? 'es-ES' : 'en-US';

    rec.onresult = (event) => {
      let interimText = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        const txt = r[0]?.transcript || '';
        if (r.isFinal) finalText += txt;
        else interimText += txt;
      }
      if (finalText.trim()) {
        onFinalChunk?.(finalText.trim());
      }
      setInterim(interimText);
    };

    rec.onerror = (event) => {
      // 'no-speech' is benign — Chrome fires it after silence; just keep going.
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      console.error('Dictation error:', event.error);
      if (event.error === 'not-allowed' || event.error === 'permission-denied') {
        toast.error(
          es
            ? 'Permiso de micrófono denegado'
            : 'Microphone permission denied',
        );
        wantsRunningRef.current = false;
        setIsListening(false);
      }
    };

    rec.onend = () => {
      // Auto-restart while user wants to keep dictating.
      setInterim('');
      if (wantsRunningRef.current) {
        try {
          rec.start();
        } catch {
          /* already started — race we can ignore */
        }
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = rec;
    return () => {
      wantsRunningRef.current = false;
      try { rec.stop(); } catch { /* noop */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep language in sync.
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = es ? 'es-ES' : 'en-US';
    }
  }, [es]);

  const start = useCallback(() => {
    const rec = recognitionRef.current;
    if (!rec) {
      toast.error(es ? 'Dictado no disponible en este navegador' : 'Dictation not available in this browser');
      return;
    }
    wantsRunningRef.current = true;
    try {
      rec.start();
      setIsListening(true);
    } catch {
      // start() throws if already running — treat as a no-op.
      setIsListening(true);
    }
  }, [es]);

  const stop = useCallback(() => {
    wantsRunningRef.current = false;
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.stop(); } catch { /* noop */ }
    }
    setIsListening(false);
    setInterim('');
  }, []);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  return {
    isListening,
    interim,
    start,
    stop,
    toggle,
    supported: supportedRef.current,
  };
}
